// Unified agent memory — types + React Query hooks (DEV-16962).
//
// Wraps the unified /v1/memories API (DEV-16810) that replaces theopolis's
// /preferences/refinement/memories/* — see lib/refinement-memory.ts, now
// dead code once every caller has migrated (DEV-16820 retires it).
//
//   GET    /v1/memories                  — list (status/agentId/appliesToAll filters)
//   GET    /v1/memories/metrics          — corpus-health snapshot
//   GET    /v1/memories/:id              — detail
//   POST   /v1/memories                  — create (manual writes auto-enter APPROVED)
//   PATCH  /v1/memories/:id              — update (copy-on-write, new id+version)
//   POST   /v1/memories/:id/approve      — PENDING|CANDIDATE → APPROVED
//   POST   /v1/memories/:id/reject       — PENDING|CANDIDATE → REJECTED (+archived)
//   POST   /v1/memories/:id/archive      — soft-archive (no DELETE — memories never hard-delete)
//
// Model differences from theopolis, by design (DEV-16805 unified store):
//   - status has FOUR states (PENDING, CANDIDATE, APPROVED, REJECTED), not three.
//     PENDING is a plain agent-authored write; CANDIDATE is a feedback-loop
//     proposal carrying a sourceRunId. Both are "awaiting review" from an
//     operator's point of view — the console's "Pending review" tab merges them.
//   - operator (manual) writes ALWAYS enter APPROVED — there is no
//     operator-staged-for-review path in the unified API (the theopolis
//     concept of a manually-created 'pending' memory is gone; only
//     agent-authored writes can be PENDING/CANDIDATE).
//   - scope is a BINDING (appliesToAll / agentIds[]), not a workflow/agent/org
//     tri-state. There is no first-class "workflow scope" binding target in
//     the unified model — a workflow-scoped memory is expressed as an
//     agent-bound row with `subjectRef.workflowId` (a within-set filter, not
//     a binding). The create UI does not yet expose subjectRef authoring
//     (DEV-16819 owns the fuller binding/isolation UX); this migration keeps
//     create to appliesToAll-or-agentIds and treats subjectRef as read-only
//     display for rows the agent itself scoped.
//
// All mutations invalidate the list query under a single coarse key
// (['memories']) — same cheap-refetch rationale as the theopolis version.

import { useClient } from '@cortex/react-query';
import type {
  AgentMemoryMetrics,
  AgentMemoryWithBindings,
  MemoryDigest,
  CreateMemoryInput,
  MemoryStatus,
  UpdateMemoryInput,
  BatchApproveMemoriesResult,
  BatchRejectMemoriesResult,
} from '@cortex/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

export type { AgentMemoryMetrics, AgentMemoryWithBindings, CreateMemoryInput, UpdateMemoryInput };

interface IPaginatedEnvelope<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface IMemoryListQuery {
  status?: MemoryStatus;
  agentId?: string;
  appliesToAll?: boolean;
  includeArchived?: boolean;
}

const memoryListKey = ['memories'] as const;

/** Query key of a single memory's detail row (`useMemoryById`). Exported so
 *  mutation callers can prime the cache for a copy-on-write successor id
 *  before switching a card to it (avoids an empty-cache flash). */
export function memoryDetailKey(id: string) {
  return [...memoryListKey, 'detail', id] as const;
}
// Collection list rows carry a memoryCount — memory writes that (un)bind
// collections must refresh them too (lib/agent-collection.ts owns the key).
const collectionListKey = ['agent-collections'] as const;

function toParams(query: IMemoryListQuery): string {
  const params = new URLSearchParams();
  if (query.status) params.set('status', query.status);
  if (query.agentId) params.set('agentId', query.agentId);
  if (query.appliesToAll !== undefined) params.set('appliesToAll', String(query.appliesToAll));
  if (query.includeArchived) params.set('includeArchived', 'true');
  // The console's small-org datasets fit in one page; a larger org
  // should paginate — bump this once a page grows past it in practice.
  params.set('limit', '200');
  return params.toString();
}

/** List memories with optional filters. Defaults to non-archived. */
export function useMemoryList(query: IMemoryListQuery = {}) {
  const client = useClient();
  const qs = toParams(query);
  return useQuery({
    queryKey: [...memoryListKey, query] as const,
    queryFn: () =>
      client.http
        .get(`v1/memories?${qs}`)
        .json<IPaginatedEnvelope<AgentMemoryWithBindings>>()
        .then((page) => page.items),
  });
}

/**
 * The exact memory an agent RESOLVES at run time (DEV-17002) — appliesToAll ∪
 * bound-to-agent ∪ via-a-collection — from the shared resolver. Use for the
 * per-agent "what this agent sees" view (correctly folds in org-wide +
 * collection-derived memory, unlike a client-side filter on the list).
 */
export function useResolvedMemory(agentId: string) {
  const client = useClient();
  return useQuery({
    queryKey: [...memoryListKey, 'resolved', agentId] as const,
    queryFn: () =>
      client.http
        .get('v1/memories/resolved', { searchParams: { agentId } })
        .json<AgentMemoryWithBindings[]>(),
    enabled: Boolean(agentId),
  });
}

export function useMemoryById(
  id: string | null,
  options?: { initialData?: AgentMemoryWithBindings },
) {
  const client = useClient();
  return useQuery({
    queryKey: memoryDetailKey(id ?? ''),
    queryFn: () => client.http.get(`v1/memories/${id}`).json<AgentMemoryWithBindings>(),
    enabled: Boolean(id),
    initialData: options?.initialData,
  });
}

/**
 * Batch-fetch a known set of ids in one request — e.g. a PA chat transcript
 * resolving several save_memory cards at once instead of firing one
 * `GET /v1/memories/:id` per card. Returns a Map so callers can look up by
 * id without re-deriving one from an array on every render.
 */
export function useMemoriesByIds(ids: string[]) {
  const client = useClient();
  const uniqueSortedIds = [...new Set(ids)].sort();
  const query = useQuery({
    queryKey: [...memoryListKey, 'by-ids', uniqueSortedIds] as const,
    queryFn: () =>
      client.http
        .get(`v1/memories?ids=${uniqueSortedIds.map(encodeURIComponent).join(',')}`)
        .json<IPaginatedEnvelope<AgentMemoryWithBindings>>()
        .then((page) => page.items),
    enabled: uniqueSortedIds.length > 0,
  });
  const data = query.data;
  return useMemo(() => new Map((data ?? []).map((m) => [m.id, m])), [data]);
}

/** One audit-trail row (DEV-17253) as the events endpoint serializes it. */
export interface IMemoryEventRow {
  id: string;
  kind: string;
  actorSubject: string | null;
  reason: string | null;
  payload: Record<string, unknown> | null;
  createdAt: string;
}

/**
 * The full per-memory trail, newest first (DEV-17256 console history panel
 * over DEV-17253's GET /v1/memories/:id/events). Fetched lazily — only when
 * a row's History toggle is open — so the list page stays one request.
 */
export function useMemoryEvents(memoryId: string, enabled: boolean) {
  const client = useClient();
  return useQuery({
    queryKey: ['memories', memoryId, 'events'] as const,
    queryFn: () => client.http.get(`v1/memories/${memoryId}/events`).json<IMemoryEventRow[]>(),
    enabled,
  });
}

export function useApproveMemory() {
  const client = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      client.http.post(`v1/memories/${id}/approve`).json<AgentMemoryWithBindings>(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: memoryListKey });
    },
  });
}

export function useRejectMemory() {
  const client = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      client.http.post(`v1/memories/${id}/reject`).json<AgentMemoryWithBindings>(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: memoryListKey });
    },
  });
}

/** Batch governance (DEV-16845) — admin-gated server-side (fde/superuser). */
export function useBatchApproveMemories() {
  const client = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      client.http
        .post('v1/memories/batch-approve', { json: { ids } })
        .json<BatchApproveMemoriesResult>(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: memoryListKey });
    },
  });
}

export function useBatchRejectMemories() {
  const client = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      client.http
        .post('v1/memories/batch-reject', { json: { ids } })
        .json<BatchRejectMemoriesResult>(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: memoryListKey });
    },
  });
}

/**
 * Update a memory (copy-on-write). The server inserts a new versioned row
 * and archives the previous one; the mutation succeeds against the OLD id,
 * the response carries the NEW row.
 */
export function useUpdateMemory() {
  const client = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateMemoryInput }) =>
      client.http.patch(`v1/memories/${id}`, { json: patch }).json<AgentMemoryWithBindings>(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: memoryListKey });
      await qc.invalidateQueries({ queryKey: collectionListKey });
    },
  });
}

/** Soft-archive. There is no delete — memories never hard-delete. */
export function useArchiveMemory() {
  const client = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      client.http.post(`v1/memories/${id}/archive`).json<AgentMemoryWithBindings>(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: memoryListKey });
      await qc.invalidateQueries({ queryKey: collectionListKey });
    },
  });
}

/**
 * Create a memory. Operator (manual) writes always enter APPROVED — there
 * is no operator-staged-pending path in the unified API. Console callers
 * must supply the binding: `appliesToAll: true` or a non-empty `agentIds`.
 */
export function useCreateMemory() {
  const client = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateMemoryInput) =>
      client.http.post('v1/memories', { json: body }).json<AgentMemoryWithBindings>(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: memoryListKey });
      await qc.invalidateQueries({ queryKey: collectionListKey });
    },
  });
}

/** Corpus-health metrics (DEV-16963). Shares the list key family for auto-invalidation. */
export function useMemoryMetrics(days: number = 30) {
  const client = useClient();
  return useQuery({
    queryKey: [...memoryListKey, 'metrics', days] as const,
    queryFn: () => client.http.get(`v1/memories/metrics?days=${days}`).json<AgentMemoryMetrics>(),
    // 60s stale window — the metrics are aggregated; refetching too
    // aggressively pushes server load without improving accuracy.
    staleTime: 60_000,
  });
}

/**
 * The review digest (DEV-17253): what agents taught themselves in the window
 * — policy-auto-approved memories, judge promotions, and the audit trail's
 * per-kind event counts. The read side of §4.7's "the trail must be read".
 */
export function useMemoryDigest(days: number = 7) {
  const client = useClient();
  return useQuery({
    queryKey: [...memoryListKey, 'digest', days] as const,
    queryFn: () => client.http.get(`v1/memories/digest?days=${days}`).json<MemoryDigest>(),
    staleTime: 60_000,
  });
}

/** Human-readable priority label for the badge text. */
export function priorityLabel(priority: number): 'normal' | 'important' | 'pinned' {
  if (priority === 2) return 'pinned';
  if (priority === 1) return 'important';
  return 'normal';
}

/** Is this memory past its expiry? The unified schema has no server-computed flag. */
export function isMemoryStale(m: AgentMemoryWithBindings): boolean {
  return m.expiresAt !== null && new Date(m.expiresAt).getTime() < Date.now();
}

/**
 * Is this row awaiting operator review? Merges PENDING (plain agent-authored)
 * and CANDIDATE (feedback-loop proposal) — both are pre-APPROVED states an
 * operator must act on; the console's "Pending review" tab shows both.
 */
export function isMemoryPendingReview(status: MemoryStatus): boolean {
  return status === 'PENDING' || status === 'CANDIDATE';
}
